import type { ActionPattern } from "@redux-saga/types";
import { getAgglomeratesForSegmentsFromTracingstore, getUpdateActionLog } from "admin/rest_api";
import features from "features";
import ErrorHandling from "libs/error_handling";
import { NumberLikeMapWrapper } from "libs/number_like_map_wrapper";
import Toast from "libs/toast";
import { sleep } from "libs/utils";
import compact from "lodash-es/compact";
import sum from "lodash-es/sum";
import { buffers, type Channel } from "redux-saga";
import {
  actionChannel,
  all,
  call,
  delay,
  flush,
  fork,
  put,
  race,
  spawn,
  takeEvery,
} from "typed-redux-saga";
import type { APIUpdateActionBatch } from "types/api_types";
import { SagaIdentifier, type Vector3 } from "viewer/constants";
import { isAnnotationEditableByNonOwners } from "viewer/model/accessors/annotation_accessor";
import {
  getSegmentationLayerByName,
  getVisibleSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import {
  getAllLoadedMeshes,
  getSegmentsForLayer,
  getVolumeTracingById,
  isMeshLoaded,
} from "viewer/model/accessors/volumetracing_accessor";
import type { Action } from "viewer/model/actions/actions";
import {
  removeMeshAction,
  showManyBucketUpdatesWarningAction,
} from "viewer/model/actions/annotation_actions";
import {
  ensureLayerMappingsAreLoadedAction,
  type SetLayerMappingsAction,
} from "viewer/model/actions/dataset_actions";
import {
  dispatchEnsureTracingsWereDiffedToSaveQueueAction,
  type EnsureHasNewestVersionAction,
  finishedApplyingMissingUpdatesAction,
  finishedRebaseAction,
  finishForwardingUpdateActionsAction,
  type NotifyAboutUpdatedBucketsAction,
  prepareRebaseAction,
  setPendingProofreadingOperationInfoAction,
  setVersionNumberAction,
  startForwardingUpdateActionsAction,
} from "viewer/model/actions/save_actions";
import { setMappingAction } from "viewer/model/actions/settings_actions";
import { applySkeletonUpdateActionsFromServerAction } from "viewer/model/actions/skeletontracing_actions";
import {
  applyVolumeUpdateActionsFromServerAction,
  setHasEditableMappingAction,
  setMappingIsLockedAction,
} from "viewer/model/actions/volumetracing_actions";
import { globalPositionToBucketPositionWithMag } from "viewer/model/helpers/position_converter";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { select, take } from "viewer/model/sagas/effect_generators";
import { ensureWkInitialized } from "viewer/model/sagas/ready_sagas";
import { Model, Store } from "viewer/singletons";
import type { NumberLike, SkeletonTracing, StoreAnnotation, VolumeTracing } from "viewer/store";
import {
  enforceExecutionAsBusyBlockingUnlessAllowed,
  takeEveryWithBatchActionSupport,
} from "../saga_helpers";
import {
  refreshAffectedMeshes,
  splitAgglomerateInMapping,
  updateMappingWithMerge,
} from "../volume/proofreading/proofread_saga";
import {
  saveQueueEntriesToServerUpdateActionBatches,
  updateSaveQueueEntriesToStateAfterRebase,
} from "./rebasing_helpers_sagas";
import { pushSaveQueueAsync } from "./save_queue_draining_saga";
import { setupSavingForAnnotation, setupSavingForTracingType } from "./save_queue_filling_saga";

function* setupSavingToServer(): Saga<void> {
  // This saga continuously drains the save queue by sending its content to the server.
  yield* fork(pushSaveQueueAsync);
  // The following sagas are responsible for filling the save queue with the update actions.
  yield* takeEvery("INITIALIZE_ANNOTATION_WITH_TRACINGS", setupSavingForAnnotation);
  yield* takeEveryWithBatchActionSupport("INITIALIZE_SKELETONTRACING", setupSavingForTracingType);
  yield* takeEveryWithBatchActionSupport("INITIALIZE_VOLUMETRACING", setupSavingForTracingType);
  yield* takeEvery("WK_READY", watchForNumberOfBucketsInSaveQueue);
}

export const VERSION_POLL_INTERVAL_COLLAB = process.env.IS_TESTING ? 500 : 10 * 1000;
const VERSION_POLL_INTERVAL_READ_ONLY = process.env.IS_TESTING ? 250 : 5 * 1000;
const VERSION_POLL_INTERVAL_SINGLE_EDITOR = process.env.IS_TESTING ? 1 * 1000 : 30 * 1000;
// interval at which the number of buckets in save queue is checked
const CHECK_NUMBER_OF_BUCKETS_IN_SAVE_QUEUE_INTERVAL_MS = 10 * 1000;
// sliding time window for which the number of buckets in save queue is summed up
const CHECK_NUMBER_OF_BUCKETS_SLIDING_WINDOW_MS = 120 * 1000;

function* watchForNumberOfBucketsInSaveQueue(): Saga<void> {
  const bucketSaveWarningThreshold = features().bucketSaveWarningThreshold;
  let bucketsForCurrentInterval = 0;
  let currentBucketCounts: Array<number> = [];
  const bucketCountArrayLength = Math.floor(
    CHECK_NUMBER_OF_BUCKETS_SLIDING_WINDOW_MS / CHECK_NUMBER_OF_BUCKETS_IN_SAVE_QUEUE_INTERVAL_MS,
  );
  yield* takeEvery("NOTIFY_ABOUT_UPDATED_BUCKETS", (action: NotifyAboutUpdatedBucketsAction) => {
    bucketsForCurrentInterval += action.count;
  });
  while (true) {
    yield* delay(CHECK_NUMBER_OF_BUCKETS_IN_SAVE_QUEUE_INTERVAL_MS);
    const sumOfBuckets = sum(currentBucketCounts);
    if (sumOfBuckets > bucketSaveWarningThreshold) {
      Store.dispatch(showManyBucketUpdatesWarningAction());
    }
    currentBucketCounts.push(bucketsForCurrentInterval);
    if (currentBucketCounts.length > bucketCountArrayLength) {
      currentBucketCounts.shift();
    }
    bucketsForCurrentInterval = 0;
  }
}

function* getPollInterval(): Saga<number> {
  const allowSave = yield* select((state) => state.annotation.restrictions.allowSave);
  if (!allowSave) {
    // The current user may not edit/save the annotation.
    return VERSION_POLL_INTERVAL_READ_ONLY;
  }

  const othersMayEdit = yield* select((state) => isAnnotationEditableByNonOwners(state.annotation));
  if (othersMayEdit) {
    // Other users may edit the annotation.
    return VERSION_POLL_INTERVAL_COLLAB;
  }
  // The current user is the only one who can edit the annotation.
  return VERSION_POLL_INTERVAL_SINGLE_EDITOR;
}

function* shouldCheckForNewerAnnotationVersions(): Saga<boolean> {
  const allowSave = yield* select(
    (state) =>
      state.annotation.restrictions.allowSave && state.annotation.isUpdatingCurrentlyAllowed,
  );
  const collaborationMode = yield* select((state) => state.annotation.collaborationMode);

  const userCanSaveAndNoLiveCollab = allowSave && collaborationMode !== "Concurrent";
  if (userCanSaveAndNoLiveCollab) {
    // The active user is currently the only one that is allowed to mutate the annotation.
    // Since we only acquire the mutex upon page load (because adhoc mutex strategy is only used
    // for concurrent collab mode), there shouldn't be any unseen updates
    // between the page load and this check here.
    // A race condition where
    //   1) another user saves version X
    //   2) we load the annotation but only get see version X - 1 (this is the race)
    //   3) we acquire a mutex
    // should not occur, because there is a grace period for which the mutex has to be free until it can
    // be acquired again (see annotation.mutex.expiryTime in application.conf).
    // The downside of an early return here is that we won't be able to warn the user early
    // if the user opened the annotation in two tabs and mutated it there.
    // However,
    //   a) this scenario is pretty rare and the worst case is that they get a 409 error
    //      during saving and
    //   b) checking for newer versions when the active user may update the annotation introduces
    //      a race condition between this saga and the actual save saga. Synchronizing these sagas
    //      would be possible, but would add further complexity to the mission critical save saga.
    return false;
  }

  // Check for tracings which could need updating
  const maybeSkeletonTracing = yield* select((state) => state.annotation.skeleton);
  const volumeTracings = yield* select((state) => state.annotation.volumes);

  const tracings: Array<SkeletonTracing | VolumeTracing> = compact([
    ...volumeTracings,
    maybeSkeletonTracing,
  ]);

  if (tracings.length === 0) {
    // If there are not tracings that could need updates, no update fetching is needed.
    return false;
  }
  return true;
}

function* fetchNewestMissingUpdateActions(): Saga<APIUpdateActionBatch[]> {
  const tracingStoreUrl = yield* select((state) => state.annotation.tracingStore.url);
  const annotationId = yield* select((state) => state.annotation.annotationId);
  const versionOnClient = yield* select((state) => {
    return state.annotation.version;
  });

  // Fetch all update actions that belong to a version that is newer than
  // versionOnClient. If there are none, the array will be empty.
  // The order is ascending in the version number ([v_n, v_(n+1), ...]).
  const newerActions = yield* call(
    getUpdateActionLog,
    tracingStoreUrl,
    annotationId,
    versionOnClient + 1,
    undefined,
    false,
    true,
  );
  return newerActions;
}

const SAVING_CONFLICT_TOAST_KEY = "save_conflicts_warning";

// This type is designed to include information needed after applying update actions via tryToIncorporateActions.
// This info is passed up the saga calling hierarchy.
// E.g. info about which auxiliary agglomerate meshes need reloading and which need to be removed.
// This info can then be used to trigger side effects after saving is done to e.g. reload the newest auxiliary agglomerate meshes.

type ApplyingUpdateArtifacts = {
  meshIdsToRemovePerLayer: Map<string, Set<number>>;
  meshIdsToLoadPerLayer: Map<string, Set<number>>;
};

type ApplyingUpdateResults = { success: boolean; artifactInfos: ApplyingUpdateArtifacts };

const FailedIncorporateActionsReturnValue: ApplyingUpdateResults = {
  success: false,
  artifactInfos: {
    meshIdsToRemovePerLayer: new Map(),
    meshIdsToLoadPerLayer: new Map(),
  },
};
const SuccessEmptyIncorporateActionsReturnValue: ApplyingUpdateResults = {
  success: true,
  artifactInfos: {
    meshIdsToRemovePerLayer: new Map(),
    meshIdsToLoadPerLayer: new Map(),
  },
};

// This function updates the agglomerate ids of source- and targetInformation from proofreading actions,
// to ensure that the post processing of a proofreading interaction by a saga in proofread_saga.tsx has
// agglomerate id information from the state where the latest backend updates were applied but the own
// mapping changes are not yet applied. This is needed to have correct information about what agglomerate ids
// were actually affected by a proofreading action done by the local user. The info correctness is essential
// to properly reload and synchronize loaded agglomerate trees and meshes.
function* updatePendingProofreadingOperationInfoAction() {
  const proofreadingPostProcessingInfo = yield* select(
    (state) => state.save.proofreadingPostProcessingInfo,
  );
  if (proofreadingPostProcessingInfo == null) {
    return;
  }
  const { tracingId, sourceInfo, targetInfo } = proofreadingPostProcessingInfo;
  const activeMapping = yield* select(
    (store) => store.temporaryConfiguration.activeMappingByLayer[tracingId],
  );

  let sourceAgglomerateId: number | undefined;
  let targetAgglomerateId: number | undefined;

  if (activeMapping.mapping != null) {
    const mappingWrapper = new NumberLikeMapWrapper(activeMapping.mapping);
    sourceAgglomerateId = mappingWrapper.getAsNumber(sourceInfo.unmappedId);
    if (targetInfo) {
      targetAgglomerateId = mappingWrapper.getAsNumber(targetInfo.unmappedId);
    }
  }

  if (sourceAgglomerateId != null && (targetInfo == null || targetAgglomerateId != null)) {
    yield* put(
      setPendingProofreadingOperationInfoAction({
        tracingId,
        sourceInfo: { ...sourceInfo, agglomerateId: sourceAgglomerateId },
        targetInfo: targetInfo
          ? {
              ...targetInfo,
              agglomerateId:
                // If targetInfo != null, targetAgglomerateId will be != null, too
                // (we ensure this in the if-condition).
                targetAgglomerateId as number,
            }
          : null,
      }),
    );
  } else {
    // In the rare case where after applying the backend updates the mapping information
    // for the source and targetInfo is no longer present in the mapping, load this missing info from the backend.
    const tracingStoreUrl = yield* select((state) => state.annotation.tracingStore.url);
    const annotationId = yield* select((state) => state.annotation.annotationId);
    const annotationVersion = yield* select((state) => state.annotation.version);
    const idsToRequest = targetInfo
      ? [sourceInfo.unmappedId, targetInfo.unmappedId]
      : [sourceInfo.unmappedId];
    const agglomerateInfoFromServer = yield* call(
      getAgglomeratesForSegmentsFromTracingstore,
      tracingStoreUrl,
      tracingId,
      idsToRequest,
      annotationId,
      annotationVersion,
    );
    const mappingWrapper = new NumberLikeMapWrapper(agglomerateInfoFromServer);
    const sourceAgglomerateIdFromServer = mappingWrapper.get(sourceInfo.unmappedId);
    const targetAgglomerateIdFromServer = targetInfo
      ? mappingWrapper.get(targetInfo.unmappedId)
      : null;

    yield* put(
      setPendingProofreadingOperationInfoAction({
        tracingId,
        sourceInfo: {
          ...sourceInfo,
          agglomerateId: Number(sourceAgglomerateIdFromServer ?? sourceInfo.agglomerateId),
        },
        targetInfo: targetInfo
          ? {
              ...targetInfo,
              agglomerateId: Number(targetAgglomerateIdFromServer ?? targetInfo.agglomerateId),
            }
          : null,
      }),
    );
  }
}

function* applyNewestMissingUpdateActions(
  actions: APIUpdateActionBatch[],
  needsRewindingRebase: boolean,
): Saga<ApplyingUpdateResults> {
  if (actions.length === 0) {
    Toast.close(SAVING_CONFLICT_TOAST_KEY);
    return SuccessEmptyIncorporateActionsReturnValue;
  }
  const allowSave = yield* select(
    (state) =>
      state.annotation.restrictions.allowSave && state.annotation.isUpdatingCurrentlyAllowed,
  );
  try {
    if (!needsRewindingRebase) {
      // If no rebasing is currently done, we still need to inform the diffing saga, that the currently replayed
      // update actions originate from the server and should not be considered during diffing.
      yield put(startForwardingUpdateActionsAction());
    }
    const { success, artifactInfos } = yield* tryToIncorporateActions(actions, false);
    // Updates the annotation state used for future rebase operation the the current state with the missingUpdateActions applied.
    yield* put(finishedApplyingMissingUpdatesAction());
    if (!needsRewindingRebase) {
      yield* put(finishForwardingUpdateActionsAction());
    }
    if (success) {
      yield* call(updatePendingProofreadingOperationInfoAction);
      return { success: true, artifactInfos };
    }
  } catch (exc) {
    // Afterwards, the user will be asked to reload the page.
    console.error("Error during application of update actions", exc);
  }

  const hasPendingUpdates = (yield* select((state) => state.save.queue)).length > 0;

  let msg = "";
  if (!allowSave) {
    msg =
      "A newer version of this annotation was found on the server. Reload the page to see the newest changes.";
  } else if (hasPendingUpdates) {
    msg =
      "A newer version of this annotation was found on the server. Your current changes to this annotation cannot be saved anymore. Please reload.";
  } else {
    msg =
      "A newer version of this annotation was found on the server. Please reload the page to see the newer version. Otherwise, changes to the annotation cannot be saved anymore.";
  }
  Toast.warning(msg, {
    sticky: true,
    key: SAVING_CONFLICT_TOAST_KEY,
  });
  return FailedIncorporateActionsReturnValue;
}

function* diffTracingsAndPrepareRebase(): Saga<void> {
  const annotation = yield* select((state) => state.annotation);
  yield dispatchEnsureTracingsWereDiffedToSaveQueueAction(Store.dispatch, annotation);
  yield* put(prepareRebaseAction());
}

function* fulfillAllEnsureHasNewestVersionActions(
  ensureHasNewestVersion: Action | undefined,
  channel: Channel<EnsureHasNewestVersionAction>,
) {
  // drain all accumulated actions at once
  const pendingActions: EnsureHasNewestVersionAction[] = yield* flush(channel);

  // include the first action we already took from the race
  const actionsToProcess = ensureHasNewestVersion
    ? [ensureHasNewestVersion, ...pendingActions]
    : pendingActions;

  for (const action of actionsToProcess) {
    (action as EnsureHasNewestVersionAction).callback();
  }
}

function* reapplyUpdateActionsFromSaveQueue(
  // appliedBackendUpdateActions contains the backend actions that were used to forward the local state
  // during rebase. These actions can be used as additional information to adapt the local, pending
  // save queue entries to the rebase.
  appliedBackendUpdateActions: APIUpdateActionBatch[],
  annotationBeforeRebase: StoreAnnotation,
): Saga<ApplyingUpdateResults> {
  const saveQueueEntries = yield* select((state) => state.save.queue);
  const currentVersion = yield* select((state) => state.annotation.version);
  if (saveQueueEntries.length === 0) {
    return SuccessEmptyIncorporateActionsReturnValue;
  }
  // Potentially update save queue entries to state after applying missing backend actions.
  // Properties like unmapped segment ids of proofreading actions might have changed and are updated here.
  // updateSaveQueueEntriesToStateAfterRebase might do some additional needed backend requests.
  const { success, updatedSaveQueue } = yield* call(
    updateSaveQueueEntriesToStateAfterRebase,
    appliedBackendUpdateActions,
    annotationBeforeRebase,
  );
  if (success) {
    const saveQueueAsServerUpdateActionBatches = saveQueueEntriesToServerUpdateActionBatches(
      updatedSaveQueue,
      currentVersion,
    );
    const { success: successfullyAppliedSaveQueueUpdates, artifactInfos } =
      yield* tryToIncorporateActions(saveQueueAsServerUpdateActionBatches, true);
    if (successfullyAppliedSaveQueueUpdates) {
      yield* put(finishedRebaseAction());
      return { success: true, artifactInfos };
    }
  }
  return FailedIncorporateActionsReturnValue;
}

type RebasingSuccessInfo = { successful: boolean; shouldTerminate: boolean };
function* performRebasingIfNecessary(): Saga<RebasingSuccessInfo> {
  const collaborationMode = yield* select((state) => state.annotation.collaborationMode);
  const missingUpdateActions = yield* call(fetchNewestMissingUpdateActions);
  // saveQueueEntries should not change during performRebasing saga. When collaborationMode==Concurrent, this is enforced via busy blocking.
  // When concurrent editing is disabled, this code typically runs in read-only mode where the save queue is empty.
  const saveQueueEntries = yield* select((state) => state.save.queue);
  const hasNewActionsFromBackend = missingUpdateActions.length > 0;

  // Side note: In a scenario where a user has an annotation open that they are not allowed to edit but another user is actively editing,
  // this code will notice that there are missingUpdateActions and apply them. This should not trigger a full "rewinding" rebase
  // and should be ensured because "not allowed to edit" means the save queue would be empty. Thus no needsRewindingRebase = true.
  const needsRewindingRebase =
    collaborationMode === "Concurrent" && hasNewActionsFromBackend && saveQueueEntries.length > 0;
  const annotationBeforeRebase = yield* select((state) => state.annotation);
  if (needsRewindingRebase) {
    // As a side-effect of this call,
    // the annotation in the store will be set to the info stored in RebaseRelevantAnnotationState
    // (similar to a git stash before doing a git pull & git stash pop).
    yield* call(diffTracingsAndPrepareRebase);
  }

  try {
    if (hasNewActionsFromBackend) {
      const applyingResult = yield* call(
        applyNewestMissingUpdateActions,
        missingUpdateActions,
        needsRewindingRebase,
      );
      if (!applyingResult.success) {
        return { successful: false, shouldTerminate: false };
      }
      yield* call(resolveApplyingUpdateArtifacts, applyingResult.artifactInfos);
    }
    if (needsRewindingRebase) {
      // If no rebasing was necessary, the pending update actions in the save queue must not be reapplied.
      const { success: successful } = yield* call(
        reapplyUpdateActionsFromSaveQueue,
        missingUpdateActions,
        annotationBeforeRebase,
      );
      if (!successful) {
        return { successful: false, shouldTerminate: false };
      }
    }
    return { successful: true, shouldTerminate: false };
  } catch (exception) {
    // If the rebasing fails for some reason, we don't want to crash the entire
    // saga.
    console.error("in save saga, got exception, terminating ...");
    console.warn(exception);
    // @ts-expect-error
    ErrorHandling.notify(exception);
    Toast.error(
      "An unrecoverable error occurred while synchronizing this annotation. Please refresh the page.",
    );
    // A hard error was thrown. Terminate this saga.
    return { successful: false, shouldTerminate: true };
  }
}
const REBASING_BUSY_BLOCK_REASON = "Syncing Annotation";

function* watchForNewerAnnotationVersion(): Saga<void> {
  yield* call(ensureWkInitialized);

  const channel = yield* actionChannel<EnsureHasNewestVersionAction>(
    ["ENSURE_HAS_NEWEST_VERSION"],
    // If multiple actions are sent to this buffer (without consumption in between),
    // we want to flush them all at once. This is achieved by using an expanding buffer
    // and flushing all events and calling their callbacks every time an ensureHasNewestVersion
    // action is resolved.
    buffers.expanding<EnsureHasNewestVersionAction>(1),
  );
  while (true) {
    // Use this annotation for rebasing the incoming update actions.
    const interval = yield* call(getPollInterval);
    let { ensureHasNewestVersion } = yield* race({
      sleep: call(sleep, interval),
      ensureHasNewestVersion: take(channel),
    });
    const shouldCheckForUpdatesOnServer = yield* call(shouldCheckForNewerAnnotationVersions);
    const isVersionRestoreActive = yield* select((state) => state.uiInformation.showVersionRestore);
    if (!shouldCheckForUpdatesOnServer || isVersionRestoreActive) {
      continue;
    }
    // In live collab mode, the user could update the annotation concurrently with rebasing.
    // Therefore, acquire the busy lock to prevent user update actions from interfering with the rebase.
    // In non-live-collab mode (typically read-only polling), skip busy blocking to avoid freezing the UI.
    const isUpdatingCurrentlyAllowed = yield* select(
      (state) => state.annotation.isUpdatingCurrentlyAllowed,
    );
    const collaborationMode = yield* select((state) => state.annotation.collaborationMode);
    const guardAsBlocking = collaborationMode === "Concurrent" && isUpdatingCurrentlyAllowed;
    const { successful, shouldTerminate } = guardAsBlocking
      ? yield* call(
          // Ensuring wk is in busy state while rebasing so no user update actions can interfere potential syncing with the backend.
          enforceExecutionAsBusyBlockingUnlessAllowed<RebasingSuccessInfo>,
          performRebasingIfNecessary,
          REBASING_BUSY_BLOCK_REASON,
          // In case another saga is already blocking the busy state, check whether the save saga is still allowed to run now or should wait for the busy flag.
          SagaIdentifier.SAVE_SAGA,
        )
      : yield* call(performRebasingIfNecessary);

    if (shouldTerminate) {
      // A hard error was thrown. Terminate this saga.
      break;
    }
    if (successful) {
      yield* call(fulfillAllEnsureHasNewestVersionActions, ensureHasNewestVersion, channel);
    } else {
      // The user was already notified about the current annotation being outdated.
      // There is not much else we can do now. Sleep for 5 minutes.
      yield* delay(5 * 60 * 1000);
    }
  }
}

export function* tryToIncorporateActions(
  newerActions: APIUpdateActionBatch[],
  areUnsavedChangesOfUser: boolean,
): Saga<{ success: boolean; artifactInfos: ApplyingUpdateArtifacts }> {
  // After all actions were incorporated, volume buckets and hdf5 mappings
  // are reloaded (if they exist and necessary). This is done as a
  // "finalization step", because it requires that the newest version is set
  // in the store annotation. Also, it only needs to happen once (instead of
  // per action).
  const refreshLayerFunctionByTracing: Record<string, () => unknown> = {};
  function* finalize() {
    for (const fn of Object.values(refreshLayerFunctionByTracing)) {
      yield* call(fn);
    }
  }

  function addToMap<T>(map: Map<string, Set<T>>, key: string, value: T) {
    if (!map.has(key)) {
      map.set(key, new Set());
    }
    map.get(key)?.add(value);
  }
  // Tracks which agglomerate ids were changed of which the frontend has loaded meshes to assist proofreading.
  // Maps from the old agglomerate id to a potentially new one.
  // Duplicates are later ignored when refreshing the meshes.
  const meshIdsToRemovePerLayer: Map<string, Set<number>> = new Map();
  const meshIdsToLoadPerLayer: Map<string, Set<number>> = new Map();

  for (const actionBatch of newerActions) {
    const agglomerateIdsToRefreshPerLayer: Map<string, Set<NumberLike>> = new Map();
    for (const action of actionBatch.value) {
      switch (action.name) {
        /////////////
        // Updates to user-specific state can be ignored if not from the active user (areUnsavedChangesOfUser = true):
        //   Camera
        case "updateCamera":
        case "updateTdCamera": {
          // Can always be ignored as not part of the rebased state, thus no replaying of the update action needed due to rebasing.
          break;
        }
        //   Active items

        //   User specific skeleton actions -- only applied if coming from current user.
        case "updateActiveNode":
        case "updateActiveTree":
        case "updateTreeVisibility":
        case "updateTreeGroupVisibility":
        case "updateUserBoundingBoxVisibilityInSkeletonTracing":
        case "updateTreeGroupsExpandedState": {
          if (areUnsavedChangesOfUser) {
            yield* put(applySkeletonUpdateActionsFromServerAction([action]));
          }
          break;
        }
        //   User specific volume actions -- only applied if coming from current user.
        case "updateActiveSegmentId":
        case "updateSegmentVisibility":
        case "updateSegmentGroupVisibility":
        case "updateUserBoundingBoxVisibilityInVolumeTracing":
        case "updateSegmentGroupsExpandedState": {
          if (areUnsavedChangesOfUser) {
            yield* put(applyVolumeUpdateActionsFromServerAction([action]));
          }
          break;
        }
        /////////////
        // Skeleton
        /////////////
        case "createTree":
        case "updateTree":
        case "createNode":
        case "createEdge":
        case "updateNode":
        case "moveTreeComponent":
        case "deleteTree":
        case "deleteEdge":
        case "deleteNode":
        case "updateTreeEdgesVisibility":
        case "updateTreeGroups":
        // Skeleton User Bounding Boxes
        case "addUserBoundingBoxInSkeletonTracing":
        case "updateUserBoundingBoxInSkeletonTracing":
        case "deleteUserBoundingBoxInSkeletonTracing": {
          yield* put(applySkeletonUpdateActionsFromServerAction([action]));
          break;
        }

        /////////////
        // Volume
        /////////////
        case "updateBucket": {
          const { value } = action;
          const cube = Model.getCubeByLayerName(value.actionTracingId);

          const dataLayer = Model.getLayerByName(value.actionTracingId);
          const bucketAddress = globalPositionToBucketPositionWithMag(
            value.position,
            value.mag,
            value.additionalCoordinates,
          );

          const bucket = cube.getBucket(bucketAddress);
          if (bucket != null && bucket.type !== "null") {
            cube.removeBucket(bucket);
            refreshLayerFunctionByTracing[value.actionTracingId] = () => {
              dataLayer.layerRenderingManager.refresh();
            };
          }
          break;
        }
        case "deleteSegmentData": {
          const { value } = action;
          const { actionTracingId, id } = value;
          const cube = Model.getCubeByLayerName(actionTracingId);
          const dataLayer = Model.getLayerByName(actionTracingId);

          cube.removeBucketsIf((bucket) => bucket.containsValue(id));
          refreshLayerFunctionByTracing[value.actionTracingId] = () => {
            dataLayer.layerRenderingManager.refresh();
          };
          break;
        }
        case "updateLargestSegmentId":
        case "createSegment":
        case "mergeSegmentItems":
        case "deleteSegment":
        case "updateSegmentPartial":
        case "updateMetadataOfSegment":
        case "upsertSegmentGroup":
        case "deleteSegmentGroup":
        // Volume User Bounding Boxes
        case "addUserBoundingBoxInVolumeTracing":
        case "deleteUserBoundingBoxInVolumeTracing":
        case "updateUserBoundingBoxInVolumeTracing": {
          yield* put(applyVolumeUpdateActionsFromServerAction([action]));
          break;
        }

        // Proofreading
        case "mergeAgglomerate": {
          const { actionTracingId, agglomerateId1, agglomerateId2 } = action.value;
          if (agglomerateId1 == null || agglomerateId2 == null) {
            console.log(
              "Cannot apply mergeAgglomerate action due to agglomerateId1 or agglomerateId2 not being provided in the action",
              action.value,
            );
            yield* call(finalize);
            return FailedIncorporateActionsReturnValue;
          }
          const activeMapping = yield* select(
            (store) => store.temporaryConfiguration.activeMappingByLayer[actionTracingId],
          );
          yield* call(
            updateMappingWithMerge,
            actionTracingId,
            activeMapping,
            agglomerateId1,
            agglomerateId2,
            !areUnsavedChangesOfUser,
          );
          if (areUnsavedChangesOfUser) {
            // As this is a self-triggered proofreading action,  the proofreading saga
            // itself takes care of reloading the meshes, no need to track this here.
            break;
          }
          const hasAnyOfBothAgglomerateMeshesLoaded = yield* select(
            (state) =>
              isMeshLoaded(state, agglomerateId1, actionTracingId) ||
              isMeshLoaded(state, agglomerateId2, actionTracingId),
          );
          if (!hasAnyOfBothAgglomerateMeshesLoaded) {
            break;
          }
          // agglomerateId2 is merged into agglomerateId1 and the frontend currently has at least one of the meshes loaded.
          // Outdate agglomerateId1 and agglomerateId2. Only agglomerateId1 needs to be reloaded however.
          // Track outdated and updated agglomerateIds to refresh after applying updates.
          addToMap(meshIdsToRemovePerLayer, actionTracingId, agglomerateId1);
          addToMap(meshIdsToRemovePerLayer, actionTracingId, agglomerateId2);
          addToMap(meshIdsToLoadPerLayer, actionTracingId, agglomerateId1);
          // Remove refresh entry of agglomerateId2 as it was merged into agglomerateId1.
          meshIdsToLoadPerLayer.get(actionTracingId)?.delete(agglomerateId2);
          break;
        }
        case "splitAgglomerate": {
          // If the changes are done by the local user, no need to do the partial refreshing of the mapping,
          // as this is done by the proofreading saga itself after saving the split actions.
          // Moreover, as the split actions are still needed to be saved after tryToIncorporateActions is finished,
          // the backend and thus a refresh within tryToIncorporateActions wouldn't yet know about the split actions and
          // thus reloading the mapping here would yield false results.
          if (areUnsavedChangesOfUser) {
            break;
          }
          // Note that a "normal" split typically contains multiple splitAgglomerate
          // actions (each action merely removes an edge in the graph).
          const { agglomerateId, actionTracingId } = action.value;
          if (agglomerateId != null) {
            // The action already contains the info about what agglomerate was split.
            // As the split could have happened between segments not loaded in this client,
            // we need to reload in case any segment of the agglomerate is loaded and
            // cannot guess the expected result without asking the backend.
            addToMap(agglomerateIdsToRefreshPerLayer, actionTracingId, agglomerateId);
          } else {
            console.log(
              "Cannot apply splitAgglomerate action due to agglomerateId not being provided in the action",
              action.value,
            );
            yield* call(finalize);
            return FailedIncorporateActionsReturnValue;
          }
          break;
        }

        case "updateMappingName": {
          const { actionTracingId, mappingName, isEditable, isLocked } = action.value;
          let mappingType;
          if (mappingName) {
            let volumeDataLayer = yield* select((state) =>
              getSegmentationLayerByName(state.dataset, actionTracingId),
            );
            // Load mappings if needed and enforce reloading if mapping is editable
            // to ensure the new mapping is available in the store data.
            if (
              volumeDataLayer.mappings == null ||
              volumeDataLayer.agglomerates == null ||
              isEditable
            ) {
              const setMappingsChannel =
                yield* actionChannel<SetLayerMappingsAction>("SET_LAYER_MAPPINGS");
              yield* put(ensureLayerMappingsAreLoadedAction(actionTracingId));
              yield* take(setMappingsChannel);
            }
            mappingType =
              (volumeDataLayer.agglomerates ?? []).indexOf(mappingName) >= 0
                ? ("HDF5" as const)
                : ("JSON" as const);
          }
          yield* put(setMappingAction(actionTracingId, mappingName, mappingType, true));
          const volume = yield* select((state) =>
            getVolumeTracingById(state.annotation, actionTracingId),
          );
          if (!volume.hasEditableMapping && isEditable) {
            yield* put(setHasEditableMappingAction(actionTracingId));
          }
          if (!volume.mappingIsLocked && isLocked) {
            yield* put(setMappingIsLockedAction(actionTracingId));
          }
          break;
        }
        /*
         * Currently NOT supported:
         */
        // TODO (#9052): These actions should be supported if applied from own save queue!

        // High-level annotation specific
        case "addLayerToAnnotation":
        case "addSegmentIndex":
        case "createTracing":
        case "deleteLayerFromAnnotation":
        case "importVolumeTracing":
        case "revertToVersion":
        case "updateLayerMetadata":
        case "updateMetadataOfAnnotation":

        // Volume
        case "removeFallbackLayer":

        // Legacy! The following actions are legacy actions and don't
        // need to be supported.
        case "mergeTree":
        case "updateSegment":
        case "updateSkeletonTracing":
        case "updateVolumeTracing":
        case "updateUserBoundingBoxesInSkeletonTracing":
        case "updateSegmentGroups":
        case "updateUserBoundingBoxesInVolumeTracing": {
          console.error("Cannot apply action", action.name);
          yield* call(finalize);
          return FailedIncorporateActionsReturnValue;
        }
        default: {
          action satisfies never;
        }
      }
    }
    yield* put(setVersionNumberAction(actionBatch.version));
    for (const [
      tracingId,
      agglomerateIdsToRefreshSet,
    ] of agglomerateIdsToRefreshPerLayer.entries()) {
      if (agglomerateIdsToRefreshSet && agglomerateIdsToRefreshSet.size > 0) {
        //  TODO (#6921): Add 64 bit support
        const agglomerateIdToRefresh = [...agglomerateIdsToRefreshSet.values().map(Number)];
        if (agglomerateIdToRefresh == null) {
          continue;
        }
        const activeMapping = yield* select(
          (store) => store.temporaryConfiguration.activeMappingByLayer[tracingId],
        );
        const splitMappingInfo = yield* splitAgglomerateInMapping(
          activeMapping,
          agglomerateIdToRefresh[0],
          tracingId,
          actionBatch.version,
          false,
          // In the very rare case where split actions of two different agglomerate ids were included in the same version
          // we also request those other agglomerate ids in the same request to save requests.
          agglomerateIdToRefresh.slice(1),
        );

        if (splitMappingInfo == null) {
          const message =
            "Failed to apply an agglomerate split action from another user. Please refresh the page to resync.";
          console.error(message);
          Toast.error(message);
          return FailedIncorporateActionsReturnValue;
        }
        const { splitMapping, oldAgglomerateIds, newAgglomerateIds } = splitMappingInfo;

        yield* put(
          setMappingAction(
            tracingId,
            activeMapping.mappingName,
            activeMapping.mappingType,
            true, // Might be optimistic. The mapping might not be in in the same state as on the server when reapplying local updates.
            // The finishedApplyingMissingUpdatesAction action takes care of storing the newest info in RebaseRelevantAnnotationState
            // after the backend updates are applied.
            {
              mapping: splitMapping || undefined,
            },
          ),
        );
        const loadedMeshes = yield* select((state) => getAllLoadedMeshes(state, tracingId));
        const loadedMeshesOfSplitAction = loadedMeshes.intersection(oldAgglomerateIds);
        if (loadedMeshesOfSplitAction.size > 0) {
          oldAgglomerateIds.forEach((aggloId) => {
            addToMap(meshIdsToRemovePerLayer, tracingId, aggloId);
          });
          newAgglomerateIds.forEach((aggloId) => {
            addToMap(meshIdsToLoadPerLayer, tracingId, aggloId);
          });
        }
      }
    }
  }

  yield* call(finalize);
  return {
    success: true,
    artifactInfos: { meshIdsToRemovePerLayer, meshIdsToLoadPerLayer },
  };
}

// Should be called with spawn to not block the rebasing as this is done detached afterwards.
function* resolveApplyingUpdateArtifacts(artifactInfos: ApplyingUpdateArtifacts) {
  const activeVolumeTracingId = (yield* select(getVisibleSegmentationLayer))?.tracingId;
  if (!activeVolumeTracingId) {
    return;
  }
  yield* call(removeOutdatedMeshes, artifactInfos.meshIdsToRemovePerLayer);
  yield* spawn(reloadMeshes, artifactInfos.meshIdsToLoadPerLayer);
}

function* removeOutdatedMeshes(
  meshIdsToRemovePerLayer: ApplyingUpdateArtifacts["meshIdsToRemovePerLayer"],
) {
  // Remove all outdated meshes.
  for (const [tracingId, meshIdsToRemove] of meshIdsToRemovePerLayer.entries()) {
    for (const aggloId of meshIdsToRemove) {
      yield* put(removeMeshAction(tracingId, Number(aggloId)));
    }
  }
}

// Potentially waits until saving is done. Thus, !must be called with spawn!.
function* reloadMeshes(
  meshIdsToReloadPerLayer: ApplyingUpdateArtifacts["meshIdsToRemovePerLayer"],
) {
  // First wait in case the ui state is busy until it is no longer to ensure a potential running proofreading saga finished.
  const busyState = yield* select((state) => state.uiInformation.busyBlockingInfo);
  if (busyState.isBusy) {
    yield* take(
      ((action: Action) =>
        action.type === "SET_BUSY_BLOCKING_INFO_ACTION" &&
        !action.value.isBusy) as ActionPattern<Action>,
    );
  }
  const refreshAffectedMeshesEffects = [];
  for (const [tracingId, meshIdsToReload] of meshIdsToReloadPerLayer.entries()) {
    const refreshList: Array<{
      newAgglomerateId: number;
      nodePosition: Vector3;
    }> = [];
    const { hasSegmentIndex } = yield* select((state) =>
      getVolumeTracingById(state.annotation, tracingId),
    );
    const segments = yield* select((state) => getSegmentsForLayer(state, tracingId));

    for (const agglomerateId of meshIdsToReload) {
      const segment = segments.getNullable(agglomerateId);
      // Only load meshes for segments still present.
      if (segment && (segment?.anchorPosition || hasSegmentIndex)) {
        refreshList.push({
          newAgglomerateId: agglomerateId,
          // If the annotation has a segment index, the seed position for the mesh generation is ignored. In that case we can simply use [0, 0, 0].
          nodePosition: segment?.anchorPosition ?? [0, 0, 0],
        });
      }
    }
    refreshAffectedMeshesEffects.push(call(refreshAffectedMeshes, tracingId, refreshList));
  }
  yield* all(refreshAffectedMeshesEffects);
}

export default [setupSavingToServer, watchForNewerAnnotationVersion];
